import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import {
  Loader2, ShieldAlert, Users, Eye, MousePointerClick, TrendingUp,
  Globe, MapPin, Smartphone, Monitor, Tablet, ArrowUpRight, RefreshCw,
  Activity, Zap, User, UserCheck, Heart, Calendar, MailCheck, MailWarning,
} from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import GeoMap from '@/components/GeoMap';
import { toast } from 'sonner';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, CartesianGrid,
} from 'recharts';

const RANGES = [
  ['24h', '24 hours'],
  ['7d', '7 days'],
  ['30d', '30 days'],
  ['90d', '90 days'],
];

const SOURCE_COLORS = {
  Direct: '#0F0F10',
  Search: '#3B82F6',
  Social: '#B91C1C',
  Ads: '#B38138',
  Email: '#15803D',
  Referral: '#8B5CF6',
  Campaign: '#D946EF',
};

const PLATFORM_COLORS = {
  Instagram: '#E1306C',
  Facebook: '#1877F2',
  TikTok: '#FE2C55',
  'Twitter / X': '#0F0F10',
  YouTube: '#FF0000',
  LinkedIn: '#0A66C2',
  Reddit: '#FF4500',
  Pinterest: '#E60023',
  Snapchat: '#F5C400',
  Threads: '#0F0F10',
  WhatsApp: '#25D366',
  Telegram: '#229ED9',
  Discord: '#5865F2',
};

const PLATFORM_INITIAL = {
  Instagram: 'IG', Facebook: 'f', TikTok: 'TT', 'Twitter / X': 'X',
  YouTube: 'YT', LinkedIn: 'in', Reddit: 'r', Pinterest: 'P',
  Snapchat: 'SC', Threads: '@', WhatsApp: 'W', Telegram: 'T', Discord: 'D',
};

function fmtNum(n) {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function Kpi({ label, value, sub, color = '#0F0F10', icon: Icon, testId }) {
  return (
    <div className="bg-[var(--surface-2)] rounded-2xl p-3 border border-[var(--hairline)]" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} style={{ color }} />}
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">{label}</p>
      </div>
      <p className="font-display text-2xl font-[700] tabular-nums" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-[#8A8A93] mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ title, icon: Icon, children, action }) {
  return (
    <section className="px-5 mt-6">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={14} className="text-[#B91C1C]" />}
        <h2 className="text-[12px] uppercase tracking-wider font-bold text-[#4B4B52]">{title}</h2>
        <div className="ml-auto">{action}</div>
      </div>
      {children}
    </section>
  );
}

function FlagEmoji({ code }) {
  if (!code || code === '??' || code.length !== 2) return <Globe size={11} className="text-[#8A8A93]" />;
  const cp = code.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65);
  return <span className="text-[14px] leading-none">{String.fromCodePoint(...cp)}</span>;
}

function DeviceIcon({ device }) {
  if (device === 'mobile') return <Smartphone size={11} />;
  if (device === 'tablet') return <Tablet size={11} />;
  if (device === 'desktop') return <Monitor size={11} />;
  return <Globe size={11} />;
}

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [onbFunnel, setOnbFunnel] = useState(null);
  const [reEng, setReEng] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [demo, setDemo] = useState(null);   // new — /admin/demographics
  const [demoSpark, setDemoSpark] = useState(null); // /admin/demographics/sparklines
  const [filterInsights, setFilterInsights] = useState(null); // Phase 2c — filter-event summary
  const [adPerf, setAdPerf] = useState(null); // Feb 2026 — video ad reward summary
  const [verifyRate, setVerifyRate] = useState(null); // Feb 2026 — email verify-rate insight
  const [paywallFunnel, setPaywallFunnel] = useState(null); // Feb 2026 — paywall conversion insight
  const [lifecycleFunnel, setLifecycleFunnel] = useState(null); // Feb 2026 — Match → Meet → Keep funnel
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/me').then(() => setAuthChecked(true))
      .catch(() => { toast.error('Admin access required'); navigate('/discover', { replace: true }); });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Sparkline window follows the selected KPI range (14 days by default).
      const sparkDays = range === '24h' ? 7 : range === '7d' ? 7 : range === '30d' ? 30 : 90;
      const [o, f, ob, re, rt, dg, sp, fi, ap, vr, pf, lf] = await Promise.all([
        api.get(`/admin/analytics/overview?range=${range}`),
        api.get(`/admin/analytics/funnel?range=${range === '24h' ? '7d' : range}`),
        api.get(`/admin/analytics/onboarding-funnel?range=${range === '24h' ? '7d' : range}`),
        api.get(`/admin/analytics/reengagement?range=${range === '24h' ? '7d' : range}`),
        api.get('/admin/analytics/realtime'),
        api.get(`/admin/demographics?date_range=${range === '24h' ? '7d' : range}`),
        api.get(`/admin/demographics/sparklines?days=${sparkDays}`),
        api.get(`/admin/analytics/filter-events?range=${range === '24h' ? '7d' : range}`),
        api.get(`/admin/analytics/ad-performance?range=${range === '24h' ? '7d' : range}`),
        api.get(`/admin/analytics/verify-rate?range=${range === '24h' ? '7d' : range}`),
        api.get(`/admin/analytics/paywall-funnel?range=${range === '24h' ? '7d' : range}`),
        api.get(`/admin/analytics/lifecycle-funnel?range=${range === '24h' ? '7d' : range}`),
      ]);
      setData(o.data);
      setFunnel(f.data);
      setOnbFunnel(ob.data);
      setReEng(re.data);
      setRealtime(rt.data);
      setDemo(dg.data);
      setDemoSpark(sp.data);
      setFilterInsights(fi.data);
      setAdPerf(ap.data);
      setVerifyRate(vr.data);
      setPaywallFunnel(pf.data);
      setLifecycleFunnel(lf.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { if (authChecked) load(); }, [authChecked, load]);

  // Auto-refresh realtime every 30s
  useEffect(() => {
    if (!authChecked) return undefined;
    const id = setInterval(() => {
      api.get('/admin/analytics/realtime').then(r => setRealtime(r.data)).catch(() => {});
    }, 30_000);
    return () => clearInterval(id);
  }, [authChecked]);

  if (!authChecked || (loading && !data)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" />
      </div>
    );
  }

  const k = data?.kpis || {};
  const trend = (data?.trend || []).map(t => ({
    day: t.day?.length === 13 ? t.day.slice(11, 13) + ':00' : (t.day || '').slice(5),
    Visitors: t.visitors,
    Pageviews: t.page_views,
  }));
  const sourcesPie = (data?.sources || []).map(s => ({
    name: s.source, value: s.visitors, fill: SOURCE_COLORS[s.source] || '#8A8A93',
  }));
  const totalSourceVisitors = sourcesPie.reduce((a, b) => a + b.value, 0) || 1;

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="px-5 pt-8 pb-4 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <BackNavButton />
        <h1 className="font-brand text-[26px] font-[600] tracking-tight">Analytics</h1>
        <button onClick={load} disabled={loading} data-testid="refresh-btn"
          className="ml-auto w-9 h-9 rounded-full bg-[var(--surface-2)] hover:bg-black/[0.05] flex items-center justify-center transition disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B91C1C]">
          <ShieldAlert size={11} /> Admin
        </span>
      </div>

      {/* Range pills */}
      <div className="px-5 pt-4 flex gap-2 overflow-x-auto no-scrollbar">
        {RANGES.map(([k, label]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition ${
              range === k ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02]'
            }`}>{label}</button>
        ))}
      </div>

      {/* Realtime sliver */}
      {realtime && (
        <div className="px-5 mt-4">
          <div className="bg-gradient-to-br from-[#B91C1C] to-[#7F1D1D] text-white rounded-2xl p-4 flex items-center gap-3" data-testid="realtime-card">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
              <div className="relative w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Activity size={16} />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">Right now · last 30 min</p>
              <p className="font-display text-2xl font-[700] tabular-nums">{realtime.visitors} visitor{realtime.visitors === 1 ? '' : 's'}</p>
              <p className="text-[11px] opacity-80">{realtime.page_views} page views</p>
            </div>
            {realtime.pages?.[0] && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">Top page</p>
                <p className="font-mono text-[12px] truncate max-w-[150px]">{realtime.pages[0].path}</p>
                <p className="text-[10px] opacity-80">{realtime.pages[0].visitors} visitor{realtime.pages[0].visitors === 1 ? '' : 's'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="px-5 mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi label="Visitors" value={fmtNum(k.visitors)} icon={Users} color="#B91C1C" testId="kpi-visitors" />
        <Kpi label="Page views" value={fmtNum(k.page_views)} icon={Eye} color="#3B82F6" testId="kpi-pageviews" />
        <Kpi label="Visit → Signup" value={`${k.conv_visit_to_signup_pct || 0}%`}
          sub={`${k.signups || 0} signups`} icon={ArrowUpRight} color="#15803D" testId="kpi-conv-signup" />
        <Kpi label="Visit → Paid" value={`${k.conv_visit_to_paid_pct || 0}%`}
          sub={`${k.new_paid || 0} new subs`} icon={Zap} color="#B38138" testId="kpi-conv-paid" />
        <Kpi label="Bounce rate" value={`${k.bounce_rate_pct || 0}%`} icon={MousePointerClick} testId="kpi-bounce" />
        <Kpi label="Pages / visitor" value={(k.avg_pages_per_visitor || 0).toFixed(2)} icon={Eye} testId="kpi-pages-per-visitor" />
        <Kpi label="Logged-in" value={fmtNum(k.logged_in_visitors)} sub={`${k.visitors ? Math.round((k.logged_in_visitors / k.visitors) * 100) : 0}% of visitors`} icon={Users} testId="kpi-logged-in" />
        <Kpi label="Range" value={range.toUpperCase()} sub="snapshot" icon={TrendingUp} color="#8A8A93" testId="kpi-range" />
      </div>

      {/* Trend chart */}
      <Section title="Visitors trend" icon={TrendingUp}>
        <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" style={{ height: 240 }}>
          {trend.length === 0 ? (
            <div className="h-full flex items-center justify-center text-[#8A8A93] text-[13px]">No data yet — visit the site as a non-admin user to start collecting.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="visGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B91C1C" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#B91C1C" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#0000000d" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#8A8A93' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#8A8A93' }} axisLine={false} tickLine={false} />
                <ReTooltip contentStyle={{ borderRadius: 12, border: '1px solid #00000014', fontSize: 12 }} />
                <Area type="monotone" dataKey="Pageviews" stroke="#3B82F6" strokeWidth={2} fill="url(#pvGrad)" />
                <Area type="monotone" dataKey="Visitors" stroke="#B91C1C" strokeWidth={2} fill="url(#visGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Section>

      {/* Sources + Devices */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-5 mt-6">
        <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52] mb-2">Source breakdown</h3>
          {sourcesPie.length === 0 ? (
            <p className="text-[12px] text-[#8A8A93] py-6 text-center">No traffic yet</p>
          ) : (
            <div className="flex items-center gap-4">
              <div style={{ width: 110, height: 110 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={sourcesPie} dataKey="value" innerRadius={28} outerRadius={50} paddingAngle={2}>
                      {sourcesPie.map((s, i) => <Cell key={i} fill={s.fill} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1.5" data-testid="sources-list">
                {sourcesPie.map((s) => (
                  <div key={s.name} className="flex items-center gap-2 text-[12px]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.fill }} />
                    <span className="font-semibold flex-1">{s.name}</span>
                    <span className="tabular-nums text-[#4B4B52]">{s.value}</span>
                    <span className="tabular-nums text-[#8A8A93] w-10 text-right">{Math.round((s.value / totalSourceVisitors) * 100)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52] mb-3">Devices</h3>
          {(data?.devices || []).length === 0 ? (
            <p className="text-[12px] text-[#8A8A93] py-6 text-center">No data</p>
          ) : (
            <div className="space-y-2" data-testid="devices-list">
              {data.devices.map((d) => {
                const total = data.devices.reduce((a, b) => a + b.visitors, 0) || 1;
                const pct = Math.round((d.visitors / total) * 100);
                return (
                  <div key={d.device} className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-white border border-[var(--hairline)] flex items-center justify-center text-[#4B4B52]">
                      <DeviceIcon device={d.device} />
                    </span>
                    <span className="text-[13px] font-semibold capitalize flex-1">{d.device}</span>
                    <div className="flex-[2] h-2 rounded-full bg-black/[0.05] overflow-hidden">
                      <div className="h-full brand-gradient" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[11px] tabular-nums w-14 text-right text-[#4B4B52]">{d.visitors} · {pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Conversion funnel */}
      {funnel?.steps && (
        <Section title="Conversion funnel" icon={ArrowUpRight}>
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)] space-y-2.5" data-testid="funnel">
            {funnel.steps.map((s, i) => {
              const max = funnel.steps[0].value || 1;
              const pct = Math.max(2, Math.round((s.value / max) * 100));
              const dropoff = i === 0 ? null : (funnel.steps[i - 1].value
                ? Math.round((s.value / funnel.steps[i - 1].value) * 100)
                : 0);
              return (
                <div key={s.step} className="flex items-center gap-3">
                  <span className="text-[12px] font-semibold w-32 text-[#0F0F10]">{s.step}</span>
                  <div className="flex-1 h-7 rounded-lg bg-black/[0.05] overflow-hidden relative">
                    <div className="h-full brand-gradient transition-all" style={{ width: `${pct}%` }} />
                    <span className="absolute inset-0 flex items-center px-2 text-[11px] font-bold text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
                      {fmtNum(s.value)}
                    </span>
                  </div>
                  <span className="text-[11px] tabular-nums w-12 text-right text-[#8A8A93]">{s.pct_of_top}%</span>
                  {dropoff != null && (
                    <span className={`text-[11px] tabular-nums w-12 text-right font-semibold ${dropoff < 30 ? 'text-[#B91C1C]' : dropoff < 60 ? 'text-[#B38138]' : 'text-[#15803D]'}`}>
                      {dropoff}%
                    </span>
                  )}
                </div>
              );
            })}
            <p className="text-[10px] text-[#8A8A93] mt-1">% of top step · step-to-step drop-off</p>
          </div>
        </Section>
      )}

      {/* Onboarding funnel — granular per-step drop-off inside ProfileSetup */}
      {onbFunnel?.steps && (
        <Section title="Onboarding funnel" icon={ArrowUpRight}>
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" data-testid="onboarding-funnel">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-[24px] font-bold tabular-nums text-[#0F0F10]">{onbFunnel.completion_rate_pct}%</span>
              <span className="text-[11px] text-[#8A8A93]">completion rate · {fmtNum(onbFunnel.completed)} of {fmtNum(onbFunnel.starters)} starters</span>
            </div>
            <div className="space-y-2.5">
              {onbFunnel.steps.map((s, i) => {
                const max = onbFunnel.steps[0].reach || 1;
                const pct = Math.max(2, Math.round((s.reach / max) * 100));
                return (
                  <div key={s.step} className="flex items-center gap-3">
                    <span className="text-[12px] font-semibold w-44 text-[#0F0F10]">{s.step}</span>
                    <div className="flex-1 h-7 rounded-lg bg-black/[0.05] overflow-hidden relative">
                      <div className="h-full brand-gradient transition-all" style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-[11px] font-bold text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
                        {fmtNum(s.reach)}
                      </span>
                    </div>
                    <span className="text-[11px] tabular-nums w-12 text-right text-[#8A8A93]">{s.reach_pct}%</span>
                    {s.advance_pct != null ? (
                      <span className={`text-[11px] tabular-nums w-12 text-right font-semibold ${s.advance_pct < 50 ? 'text-[#B91C1C]' : s.advance_pct < 75 ? 'text-[#B38138]' : 'text-[#15803D]'}`} title="advance to next step">
                        {s.advance_pct}%
                      </span>
                    ) : (
                      <span className="text-[11px] w-12 text-right text-[#8A8A93]">—</span>
                    )}
                    {i < onbFunnel.steps.length - 1 && s.stuck > 0 ? (
                      <span className="text-[10px] tabular-nums w-20 text-right text-[#B91C1C] font-medium" title="users currently stuck on this step">
                        {fmtNum(s.stuck)} stuck
                      </span>
                    ) : (
                      <span className="text-[10px] w-20 text-right text-transparent">—</span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-[#8A8A93] mt-3">
              Reach % of starters · step-to-step advance % · users currently stuck on each step. Lower advance % = where to optimize.
            </p>
          </div>
        </Section>
      )}

      {/* Demographics + Dates */}
      {demo && <DemographicsSection demo={demo} spark={demoSpark} />}

      {/* Re-engagement email recovery */}
      {reEng && (
        <Section title="Re-engagement recovery" icon={ArrowUpRight}>
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" data-testid="reengagement-recovery">
            <div className="flex items-baseline gap-3 mb-3 flex-wrap">
              <span className="text-[24px] font-bold tabular-nums text-[#0F0F10]">{reEng.recovery_rate_pct}%</span>
              <span className="text-[11px] text-[#8A8A93]">
                recovery rate · {fmtNum(reEng.recovered)} of {fmtNum(reEng.sent)} stalled users finished onboarding after the email
              </span>
            </div>
            {reEng.sent === 0 ? (
              <p className="text-[12px] text-[#8A8A93]">
                No re-engagement emails sent in this range yet. The cron runs daily at 17:00 UTC and only targets drafts 24h–7d old.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {reEng.by_step.map((s) => (
                  <div key={s.step} className="bg-white/60 rounded-xl p-3 border border-[var(--hairline)]">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93] truncate">{s.label}</p>
                    <p className="text-[20px] font-bold tabular-nums mt-1 text-[#0F0F10]">{s.recovery_rate_pct}%</p>
                    <p className="text-[10px] text-[#8A8A93] tabular-nums">{fmtNum(s.recovered)} / {fmtNum(s.sent)} sent</p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-[#8A8A93] mt-3">
              Per-step recovery rate · which drop-off message converts best. Highest % = the copy worth A/B-replicating.
            </p>
          </div>
        </Section>
      )}

      {/* Premium Filter Conversion Insights (Phase 2c) */}
      {filterInsights && (
        <Section title="Filter conversion insights" icon={ArrowUpRight}>
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" data-testid="filter-insights">
            {/* Hero metrics row */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white/60 rounded-xl p-3 border border-[var(--hairline)]" data-testid="filter-insights-conversion">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Paywall → Paid</p>
                <p className="text-[22px] font-bold tabular-nums mt-1 text-[#15803D]">{filterInsights.conversion_rate_pct}%</p>
                <p className="text-[10px] text-[#8A8A93]">
                  {fmtNum(filterInsights.totals.paywall_converted_from_filter || 0)} of {fmtNum(filterInsights.totals.paywall_shown_from_filter || 0)}
                </p>
              </div>
              <div className="bg-white/60 rounded-xl p-3 border border-[var(--hairline)]" data-testid="filter-insights-taps">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Locked-filter taps</p>
                <p className="text-[22px] font-bold tabular-nums mt-1 text-[#0F0F10]">{fmtNum(filterInsights.totals.locked_filter_tapped || 0)}</p>
                <p className="text-[10px] text-[#8A8A93]">
                  {fmtNum(filterInsights.totals.paywall_upgrade_clicked || 0)} clicked &ldquo;Upgrade&rdquo;
                </p>
              </div>
              <div className="bg-white/60 rounded-xl p-3 border border-[var(--hairline)]" data-testid="filter-insights-tier-mix">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Paywalls shown</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-[16px] font-bold tabular-nums text-[#B91C1C]">{fmtNum(filterInsights.target_tier_breakdown.premium || 0)}</span>
                  <span className="text-[10px] text-[#8A8A93]">Premium</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[16px] font-bold tabular-nums text-[#B38138]">{fmtNum(filterInsights.target_tier_breakdown.vip || 0)}</span>
                  <span className="text-[10px] text-[#8A8A93]">VIP</span>
                </div>
              </div>
            </div>

            {/* Top locked filters ranked */}
            {filterInsights.top_locked_filters.length === 0 ? (
              <p className="text-[12px] text-[#8A8A93] text-center py-4">
                No locked-filter taps yet in this range. Once users bump into premium filters, we'll show which ones drive the most upgrade clicks.
              </p>
            ) : (
              <div>
                <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52] mb-2">Top locked filters (drives paywall)</h3>
                <div className="space-y-1.5" data-testid="filter-insights-top-list">
                  {filterInsights.top_locked_filters.slice(0, 10).map((row, i) => {
                    const max = filterInsights.top_locked_filters[0]?.count || 1;
                    const pct = (row.count / max) * 100;
                    return (
                      <div key={row.filter_key} className="flex items-center gap-2 text-[13px]" data-testid={`filter-insights-row-${row.filter_key}`}>
                        <span className="text-[10px] font-bold text-[#8A8A93] w-4 tabular-nums">{i + 1}</span>
                        <span className="font-medium text-[#0F0F10] w-32 truncate capitalize">{row.filter_key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <div className="flex-1 h-2 bg-[var(--hairline)] rounded-full overflow-hidden">
                          <div className="h-full brand-gradient" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[11px] font-bold text-[#B91C1C] w-10 text-right tabular-nums">{fmtNum(row.count)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Saved search activity */}
            {(filterInsights.totals.saved_search_created > 0 || filterInsights.totals.saved_search_loaded > 0) && (
              <div className="mt-4 pt-3 border-t border-[var(--hairline)] grid grid-cols-4 gap-2" data-testid="filter-insights-saved-searches">
                <div className="text-center">
                  <p className="text-[16px] font-bold tabular-nums text-[#0F0F10]">{fmtNum(filterInsights.totals.saved_search_created || 0)}</p>
                  <p className="text-[9px] uppercase tracking-wider text-[#8A8A93]">Created</p>
                </div>
                <div className="text-center">
                  <p className="text-[16px] font-bold tabular-nums text-[#0F0F10]">{fmtNum(filterInsights.totals.saved_search_loaded || 0)}</p>
                  <p className="text-[9px] uppercase tracking-wider text-[#8A8A93]">Loaded</p>
                </div>
                <div className="text-center">
                  <p className="text-[16px] font-bold tabular-nums text-[#0F0F10]">{fmtNum(filterInsights.totals.saved_search_set_default || 0)}</p>
                  <p className="text-[9px] uppercase tracking-wider text-[#8A8A93]">Set default</p>
                </div>
                <div className="text-center">
                  <p className="text-[16px] font-bold tabular-nums text-[#0F0F10]">{fmtNum(filterInsights.totals.saved_search_deleted || 0)}</p>
                  <p className="text-[9px] uppercase tracking-wider text-[#8A8A93]">Deleted</p>
                </div>
              </div>
            )}

            <p className="text-[10px] text-[#8A8A93] mt-3">
              Which locked filters drive upgrade intent · rank #1 is the filter to promote hardest in your paywall copy.
            </p>
          </div>
        </Section>
      )}

      {/* Email verify-rate insight (Feb 2026 — surface stranded-user
          count + trend + one-click Nudge action). */}
      {verifyRate && (
        <Section title="Email verify rate" icon={MailCheck}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* KPI tiles */}
            <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" data-testid="verify-rate-kpis">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">24h verify rate</p>
                  <p className={`text-[28px] font-black tabular-nums mt-1 ${verifyRate.verify_rate_24h >= 60 ? 'text-[#15803D]' : verifyRate.verify_rate_24h >= 30 ? 'text-[#B38138]' : 'text-[#B91C1C]'}`}>
                    {verifyRate.verify_rate_24h}%
                  </p>
                  <p className="text-[11px] text-[#8A8A93] mt-0.5">
                    {fmtNum(verifyRate.verified_within_24h)} of {fmtNum(verifyRate.signups_in_window)} signups
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Ever-verified</p>
                  <p className="text-[28px] font-black tabular-nums mt-1 text-[#0F0F10]">
                    {verifyRate.verify_rate_ever}%
                  </p>
                  <p className="text-[11px] text-[#8A8A93] mt-0.5">
                    {fmtNum(verifyRate.verified_ever)} verified all-time (window)
                  </p>
                </div>
              </div>
            </div>

            {/* Stranded users + nudge CTA */}
            <div className="bg-white rounded-2xl p-4 border border-[#B45309]/25" data-testid="verify-rate-stranded">
              <div className="flex items-center gap-2 mb-2">
                <MailWarning size={14} className="text-[#B45309]" />
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#B45309]">Stranded right now</p>
              </div>
              <p className="text-[28px] font-black tabular-nums text-[#B45309]">{fmtNum(verifyRate.stranded_now)}</p>
              <p className="text-[11px] text-[#8A8A93] mt-1">
                {fmtNum(verifyRate.stranded_nudgeable)} nudgeable (not nudged in 30d)
              </p>
              <button
                data-testid="verify-rate-nudge-btn"
                disabled={nudgeBusy || verifyRate.stranded_nudgeable === 0}
                onClick={async () => {
                  const ok = window.confirm(`Send a re-engagement email to ${verifyRate.stranded_nudgeable} stranded users?`);
                  if (!ok) return;
                  setNudgeBusy(true);
                  try {
                    const r = await api.post('/admin/nudge/stranded-emails');
                    toast.success(`Nudge sent to ${r.data.sent} users (${r.data.failed} failed)`);
                    const vr = await api.get(`/admin/analytics/verify-rate?range=${range === '24h' ? '7d' : range}`);
                    setVerifyRate(vr.data);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Nudge failed');
                  } finally {
                    setNudgeBusy(false);
                  }
                }}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#B45309] text-white text-[12px] font-bold py-2 hover:bg-[#92400E] disabled:opacity-50 transition"
              >
                {nudgeBusy ? <Loader2 size={12} className="animate-spin" /> : <MailCheck size={12} />}
                {nudgeBusy ? 'Sending…' : 'Nudge stranded users'}
              </button>
            </div>

            {/* Daily trend chart (30d) */}
            <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" data-testid="verify-rate-trend">
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93] mb-2">24h verify rate — last 30 days</p>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={verifyRate.daily} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="verifyRateGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#B91C1C" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#B91C1C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" hide />
                  <YAxis domain={[0, 100]} hide />
                  <ReTooltip
                    formatter={(v, k) => k === 'rate' ? [`${v}%`, '24h verify rate'] : [v, k]}
                    labelFormatter={(l) => l}
                  />
                  <Area type="monotone" dataKey="rate" stroke="#B91C1C" strokeWidth={1.5} fill="url(#verifyRateGrad)" />
                </AreaChart>
              </ResponsiveContainer>
              <p className="text-[10px] text-[#8A8A93] mt-1">
                Peak day: {verifyRate.daily.reduce((a, b) => (b.rate > a.rate ? b : a), verifyRate.daily[0] || {rate:0}).day || '—'}
              </p>
            </div>
          </div>
        </Section>
      )}


      {/* Match → Meet → Keep funnel — defining loop of MTK (Feb 2026). */}
      {lifecycleFunnel && (
        <Section title="Match → Meet → Keep" icon={TrendingUp}>
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" data-testid="lifecycle-funnel">
            {/* Stage counts strip */}
            <div className="grid grid-cols-5 gap-3 mb-5">
              {[
                { key: 'matches',      label: 'Matches',       value: lifecycleFunnel.matches,      color: '#0F0F10' },
                { key: 'mutual_meets', label: 'Mutual Meets',  value: lifecycleFunnel.mutual_meets, color: '#B38138' },
                { key: 'mutual_mets',  label: 'Confirmed Met', value: lifecycleFunnel.mutual_mets,  color: '#15803D' },
                { key: 'mutual_keeps', label: 'Keeps ❤️',       value: lifecycleFunnel.mutual_keeps, color: '#B91C1C' },
                { key: 'multi_keep',  label: '2nd+ Keep',      value: lifecycleFunnel.multi_keep,   color: '#1E4FD8' },
              ].map(s => (
                <div key={s.key} data-testid={`lifecycle-${s.key}`}
                     className="bg-white/70 rounded-xl p-3 border border-[var(--hairline)]">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">{s.label}</p>
                  <p className="text-[22px] font-black tabular-nums mt-1" style={{ color: s.color }}>{fmtNum(s.value)}</p>
                </div>
              ))}
            </div>
            {/* Conversion rates */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { key: 'match_to_meet_rate',  label: 'Match → Meet',  value: lifecycleFunnel.match_to_meet_rate },
                { key: 'meet_to_met_rate',    label: 'Meet → Met',    value: lifecycleFunnel.meet_to_met_rate },
                { key: 'met_to_keep_rate',    label: 'Met → Keep',    value: lifecycleFunnel.met_to_keep_rate },
                { key: 'match_to_keep_rate',  label: 'Match → Keep',  value: lifecycleFunnel.match_to_keep_rate },
              ].map(r => (
                <div key={r.key} data-testid={`lifecycle-rate-${r.key}`}
                     className="bg-white/70 rounded-xl p-3 border border-[var(--hairline)]">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">{r.label}</p>
                  <p className={`text-[22px] font-black tabular-nums mt-1 ${r.value >= 20 ? 'text-[#15803D]' : r.value >= 10 ? 'text-[#B38138]' : r.value > 0 ? 'text-[#0F0F10]' : 'text-[#8A8A93]'}`}>
                    {r.value}%
                  </p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#8A8A93] mt-3">
              Tip: Match → Meet is the biggest lever. If Meet → Met is low, users are agreeing then flaking — surface a date planner nudge earlier.
            </p>
          </div>
        </Section>
      )}


      {/* Paywall conversion funnel — which `?reason=` codes sell (Feb 2026). */}
      {paywallFunnel && (
        <Section title="Paywall conversion" icon={TrendingUp}>
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" data-testid="paywall-funnel">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Views</p>
                <p className="text-[24px] font-black tabular-nums">{fmtNum(paywallFunnel.total_views)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Conversions</p>
                <p className="text-[24px] font-black tabular-nums text-[#15803D]">{fmtNum(paywallFunnel.total_conversions)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Overall rate</p>
                <p className={`text-[24px] font-black tabular-nums ${paywallFunnel.overall_rate >= 10 ? 'text-[#15803D]' : paywallFunnel.overall_rate >= 5 ? 'text-[#B38138]' : 'text-[#B91C1C]'}`}>
                  {paywallFunnel.overall_rate}%
                </p>
              </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-[var(--hairline)]">
              <table className="w-full text-[12px]">
                <thead className="bg-white/60 text-[#8A8A93] uppercase tracking-wider text-[10px] font-bold">
                  <tr><th className="text-left px-3 py-2">Reason</th><th className="text-left px-3 py-2">Feature</th><th className="text-right px-3 py-2">Views</th><th className="text-right px-3 py-2">Conv.</th><th className="text-right px-3 py-2">Rate</th><th className="text-right px-3 py-2">VIP / Prem</th></tr>
                </thead>
                <tbody>
                  {paywallFunnel.by_reason.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-4 text-center text-[#8A8A93]">No paywall views in this window yet.</td></tr>
                  )}
                  {paywallFunnel.by_reason.map((r) => (
                    <tr key={r.reason} className="border-t border-[var(--hairline)]">
                      <td className="px-3 py-2 font-mono text-[#0F0F10]">{r.reason}</td>
                      <td className="px-3 py-2 text-[#4B4B52]">{r.feature}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.views)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.conversions)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.conversion_rate >= 10 ? 'text-[#15803D]' : r.conversion_rate >= 5 ? 'text-[#B38138]' : 'text-[#B91C1C]'}`}>{r.conversion_rate}%</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#8A8A93]">{r.vip_conversions} / {r.premium_conversions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-[#8A8A93] mt-3">
              Tip: reasons with high views + low rate are copy problems. Reasons with high rate + low views are gates that need to be more prominent.
            </p>
          </div>
        </Section>
      )}


      {/* Ad performance (Feb 2026 — video ad slot / 12 Laws of Dating Dynamics) */}
      {adPerf && (
        <Section title="Ad performance" icon={Zap}>
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" data-testid="ad-performance">
            {adPerf.total_grants === 0 ? (
              <p className="text-[12px] text-[#8A8A93] text-center py-4">
                No ad rewards granted in this range yet. Once free users start watching the video ad slot to completion, you'll see reward mix + top recipients here.
              </p>
            ) : (
              <>
                {/* Hero KPIs */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-white/60 rounded-xl p-3 border border-[var(--hairline)]" data-testid="ad-perf-total">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Total rewards</p>
                    <p className="text-[22px] font-bold tabular-nums mt-1 text-[#0F0F10]">{fmtNum(adPerf.total_grants)}</p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3 border border-[var(--hairline)]" data-testid="ad-perf-unique">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Unique earners</p>
                    <p className="text-[22px] font-bold tabular-nums mt-1 text-[#0F0F10]">{fmtNum(adPerf.unique_recipients)}</p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3 border border-[var(--hairline)]" data-testid="ad-perf-avg">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Avg / earner</p>
                    <p className="text-[22px] font-bold tabular-nums mt-1 text-[#0F0F10]">
                      {adPerf.unique_recipients > 0 ? (adPerf.total_grants / adPerf.unique_recipients).toFixed(1) : '0'}
                    </p>
                  </div>
                </div>

                {/* Reward mix bar chart */}
                <div className="mb-4">
                  <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52] mb-2">Reward mix</h3>
                  <div className="space-y-1.5" data-testid="ad-perf-mix">
                    {Object.entries(adPerf.by_reward)
                      .sort((a, b) => b[1].count - a[1].count)
                      .map(([key, val]) => {
                        const max = Math.max(...Object.values(adPerf.by_reward).map(v => v.count));
                        const pct = (val.count / max) * 100;
                        return (
                          <div key={key} className="flex items-center gap-2 text-[13px]" data-testid={`ad-perf-mix-${key}`}>
                            <span className="font-medium text-[#0F0F10] w-40 truncate">{val.label || key}</span>
                            <div className="flex-1 h-2 bg-[var(--hairline)] rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-[#B45309] to-[#D97706]" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[11px] font-bold text-[#B45309] w-14 text-right tabular-nums">
                              {fmtNum(val.count)}
                              {val.amount_total > val.count && <span className="text-[#8A8A93] font-normal"> ({val.amount_total})</span>}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Top recipients */}
                {adPerf.top_recipients.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52] mb-2">Top earners</h3>
                    <div className="space-y-1" data-testid="ad-perf-top">
                      {adPerf.top_recipients.slice(0, 5).map((u, i) => (
                        <div key={u.user_id} className="flex items-center justify-between text-[12px] py-1" data-testid={`ad-perf-top-${i}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-[10px] font-bold text-[#8A8A93] w-4 tabular-nums">{i + 1}</span>
                            <a href={`/admin/users/${u.user_id}`} className="text-[#0F0F10] font-mono text-[11px] truncate hover:text-[#B91C1C]">{u.user_id}</a>
                          </div>
                          <span className="text-[11px] font-bold text-[#B91C1C] tabular-nums">{u.count}× rewards</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-[10px] text-[#8A8A93] mt-3">
                  Rewards granted from the video ad slot on Discover · free-tier only · 5-min server cooldown. Users earning many rewards are highly engaged free users — good candidates for a targeted upgrade offer.
                </p>
              </>
            )}
          </div>
        </Section>
      )}


      {/* Social platforms breakdown */}
      <Section title="Social platforms" icon={Activity}>
        {(data?.social_platforms || []).length === 0 ? (
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
            <p className="text-[12px] text-[#8A8A93] text-center">
              No social traffic in this range yet. Once people start clicking through from Instagram, TikTok, Facebook, etc., you'll see per-platform breakdowns + conversion rates here.
            </p>
          </div>
        ) : (
          <div className="bg-[var(--surface-2)] rounded-2xl border border-[var(--hairline)] overflow-hidden" data-testid="social-platforms">
            <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] uppercase tracking-wider font-bold text-[#8A8A93] border-b border-[var(--hairline)] bg-white/40">
              <div className="col-span-4">Platform</div>
              <div className="col-span-2 text-right">Visitors</div>
              <div className="col-span-2 text-right">Signups</div>
              <div className="col-span-2 text-right">→ Signup</div>
              <div className="col-span-2 text-right">→ Paid</div>
            </div>
            {data.social_platforms.map((sp) => {
              const color = PLATFORM_COLORS[sp.platform] || '#8A8A93';
              const initial = PLATFORM_INITIAL[sp.platform] || sp.platform.slice(0, 2);
              return (
                <div key={sp.platform} className="grid grid-cols-12 items-center px-4 py-2.5 border-b border-[var(--hairline)] last:border-b-0 text-[13px] hover:bg-black/[0.02] transition" data-testid={`social-row-${sp.platform.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
                  <div className="col-span-4 flex items-center gap-2.5">
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-[10px] font-extrabold tracking-tight"
                      style={{ background: color }}>
                      {initial}
                    </span>
                    <span className="font-semibold truncate">{sp.platform}</span>
                  </div>
                  <div className="col-span-2 text-right tabular-nums font-semibold">{fmtNum(sp.visitors)}</div>
                  <div className="col-span-2 text-right tabular-nums text-[#4B4B52]">{fmtNum(sp.signups)}</div>
                  <div className={`col-span-2 text-right tabular-nums font-semibold ${sp.conv_signup_pct > 0 ? 'text-[#15803D]' : 'text-[#A1A1AA]'}`}>
                    {sp.conv_signup_pct}%
                  </div>
                  <div className={`col-span-2 text-right tabular-nums font-semibold ${sp.conv_paid_pct > 0 ? 'text-[#B38138]' : 'text-[#A1A1AA]'}`}>
                    {sp.conv_paid_pct}%
                  </div>
                </div>
              );
            })}
            <p className="px-4 py-2.5 text-[10px] text-[#8A8A93] bg-white/40">
              Tagging your social links with <span className="font-mono">?utm_source=instagram&utm_medium=social</span> tracks them here too — plus they isolate as a campaign in the chart above.
            </p>
          </div>
        )}
      </Section>

      {/* Geo: countries & cities */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-5 mt-6">
        <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52] mb-3 flex items-center gap-1.5"><Globe size={11} /> Top countries</h3>
          {(data?.countries || []).length === 0 ? (
            <p className="text-[12px] text-[#8A8A93] py-6 text-center">No traffic yet</p>
          ) : (
            <div className="space-y-1.5" data-testid="countries-list">
              {data.countries.map((c) => {
                const max = data.countries[0].visitors || 1;
                const pct = Math.max(3, Math.round((c.visitors / max) * 100));
                return (
                  <div key={`${c.country}-${c.code}`} className="flex items-center gap-2 text-[12px]">
                    <FlagEmoji code={c.code} />
                    <span className="font-semibold flex-1 truncate">{c.country}</span>
                    <div className="flex-[1.5] h-1.5 bg-black/[0.05] rounded-full overflow-hidden">
                      <div className="h-full bg-[#B91C1C]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="tabular-nums w-10 text-right text-[#4B4B52]">{c.visitors}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52] mb-3 flex items-center gap-1.5"><MapPin size={11} /> Top cities</h3>
          {(data?.cities || []).length === 0 ? (
            <p className="text-[12px] text-[#8A8A93] py-6 text-center">No city data yet</p>
          ) : (
            <div className="space-y-1.5" data-testid="cities-list">
              {data.cities.map((c) => (
                <div key={`${c.city}-${c.country}`} className="flex items-center gap-2 text-[12px]">
                  <span className="font-semibold flex-1 truncate">{c.city}</span>
                  <span className="text-[#8A8A93] truncate max-w-[100px]">{c.country}</span>
                  <span className="tabular-nums w-10 text-right text-[#4B4B52]">{c.visitors}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top cities by paid conversion */}
      <Section title="Top cities by paid conversion" icon={Zap}>
        {(data?.paid_cities || []).length === 0 ? (
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
            <p className="text-[12px] text-[#8A8A93] text-center">
              No paying subscribers with geo data yet in this range. (Cities need at least 3 visitors AND at least one active paid subscriber to appear here.)
            </p>
          </div>
        ) : (
          <div className="bg-[var(--surface-2)] rounded-2xl border border-[var(--hairline)] overflow-hidden" data-testid="paid-cities">
            <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] uppercase tracking-wider font-bold text-[#8A8A93] border-b border-[var(--hairline)] bg-white/40">
              <div className="col-span-5">City</div>
              <div className="col-span-2 text-right">Visitors</div>
              <div className="col-span-2 text-right">Signups</div>
              <div className="col-span-1 text-right">Paid</div>
              <div className="col-span-2 text-right">→ Paid %</div>
            </div>
            {data.paid_cities.map((c) => (
              <div key={`${c.city}-${c.country}`}
                className="grid grid-cols-12 items-center px-4 py-2.5 border-b border-[var(--hairline)] last:border-b-0 text-[13px] hover:bg-black/[0.02] transition"
                data-testid={`paid-city-${c.city.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
                <div className="col-span-5 flex items-center gap-2 min-w-0">
                  <MapPin size={11} className="text-[#B91C1C] flex-shrink-0" />
                  <span className="font-semibold truncate">{c.city}</span>
                  <span className="text-[11px] text-[#8A8A93] truncate">{c.country}</span>
                </div>
                <div className="col-span-2 text-right tabular-nums text-[#4B4B52]">{fmtNum(c.visitors)}</div>
                <div className="col-span-2 text-right tabular-nums text-[#4B4B52]">{fmtNum(c.signups)}</div>
                <div className="col-span-1 text-right tabular-nums font-bold text-[#B38138]">{fmtNum(c.paid)}</div>
                <div className="col-span-2 text-right tabular-nums font-semibold text-[#15803D]">{c.conv_paid_pct}%</div>
              </div>
            ))}
            <p className="px-4 py-2.5 text-[10px] text-[#8A8A93] bg-white/40">
              Ranked by absolute paid count, then conversion rate. Min 3 visitors per city. Cities with 0 paid subs are hidden. Use this to pick geo-targeted ad markets — cities at the top convert best.
            </p>
          </div>
        )}
      </Section>

      {/* Referrers + Pages */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-5 mt-6">
        <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52] mb-3">Top referrers</h3>
          {(data?.referrers || []).length === 0 ? (
            <p className="text-[12px] text-[#8A8A93] py-6 text-center">All traffic direct</p>
          ) : (
            <div className="space-y-1.5" data-testid="referrers-list">
              {data.referrers.map((r) => (
                <div key={r.label} className="flex items-center gap-2 text-[12px]">
                  <span className="font-semibold flex-1 truncate">{r.label}</span>
                  <span className="tabular-nums w-12 text-right text-[#4B4B52]">{r.visitors}</span>
                  <span className="tabular-nums w-12 text-right text-[#8A8A93]">{r.views}vw</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52] mb-3">Top pages</h3>
          {(data?.pages || []).length === 0 ? (
            <p className="text-[12px] text-[#8A8A93] py-6 text-center">No data</p>
          ) : (
            <div className="space-y-1.5" data-testid="pages-list">
              {data.pages.map((p) => {
                const max = data.pages[0].views || 1;
                const pct = Math.max(3, Math.round((p.views / max) * 100));
                return (
                  <div key={p.path} className="flex items-center gap-2 text-[12px]">
                    <span className="font-mono text-[11px] flex-1 truncate text-[#0F0F10]">{p.path}</span>
                    <div className="flex-[1] h-1.5 bg-black/[0.05] rounded-full overflow-hidden">
                      <div className="h-full bg-[#3B82F6]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="tabular-nums w-10 text-right text-[#4B4B52]">{p.views}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Campaigns */}
      <Section title="Active campaigns" icon={Zap}>
        {(data?.campaigns || []).length === 0 ? (
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
            <p className="text-[12px] text-[#8A8A93] text-center">
              No campaigns tracked yet. Add UTM params to your ad / email links — e.g. <span className="font-mono text-[11px] bg-white px-1.5 py-0.5 rounded">?utm_source=instagram&utm_medium=social&utm_campaign=spring2026</span>
            </p>
          </div>
        ) : (
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]" style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.campaigns} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0000000d" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#8A8A93' }} axisLine={false} tickLine={false} />
                <YAxis dataKey="campaign" type="category" tick={{ fontSize: 10, fill: '#0F0F10' }} axisLine={false} tickLine={false} width={120} />
                <ReTooltip contentStyle={{ borderRadius: 12, border: '1px solid #00000014', fontSize: 12 }} />
                <Bar dataKey="visitors" name="Visitors" fill="#B91C1C" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      {/* Ad Performance — ROI per campaign */}
      <Section title="Ad performance · ROI per campaign" icon={ArrowUpRight}>
        {(data?.ad_performance || []).length === 0 ? (
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
            <p className="text-[12px] text-[#8A8A93] text-center">
              No campaign data yet. Tag your ad / email links with <span className="font-mono text-[11px] bg-white px-1.5 py-0.5 rounded">?utm_source=instagram&utm_medium=cpc&utm_campaign=spring_launch</span> — once subscribers come in via tagged links, you'll see revenue per campaign here.
            </p>
          </div>
        ) : (
          <div className="bg-[var(--surface-2)] rounded-2xl border border-[var(--hairline)] overflow-x-auto" data-testid="ad-performance">
            <div className="min-w-[680px]">
              <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] uppercase tracking-wider font-bold text-[#8A8A93] border-b border-[var(--hairline)] bg-white/40">
                <div className="col-span-4">Campaign</div>
                <div className="col-span-1 text-right">Visitors</div>
                <div className="col-span-1 text-right">Signups</div>
                <div className="col-span-1 text-right">Paid</div>
                <div className="col-span-1 text-right">→ Paid</div>
                <div className="col-span-2 text-right">MRR</div>
                <div className="col-span-2 text-right">Annual run-rate</div>
              </div>
              {data.ad_performance.map((r, i) => {
                const key = `${r.source}-${r.medium}-${r.campaign}-${i}`;
                const sourceColor = (r.medium || '').toLowerCase() === 'cpc' || (r.medium || '').toLowerCase() === 'paid' ? '#B38138'
                  : (r.medium || '').toLowerCase() === 'social' ? '#B91C1C'
                  : (r.medium || '').toLowerCase() === 'email' ? '#15803D'
                  : '#3B82F6';
                return (
                  <div key={key}
                    className="grid grid-cols-12 items-center px-4 py-2.5 border-b border-[var(--hairline)] last:border-b-0 text-[13px] hover:bg-black/[0.02] transition"
                    data-testid={`ad-row-${i}`}>
                    <div className="col-span-4 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: sourceColor }} />
                        <span className="font-semibold truncate">{r.campaign || '(no campaign)'}</span>
                      </div>
                      <p className="text-[10px] text-[#8A8A93] mt-0.5 truncate ml-3.5">
                        {r.source || '—'} · {r.medium || 'unknown medium'}
                      </p>
                    </div>
                    <div className="col-span-1 text-right tabular-nums text-[#4B4B52]">{fmtNum(r.visitors)}</div>
                    <div className="col-span-1 text-right tabular-nums text-[#4B4B52]">{fmtNum(r.signups)}</div>
                    <div className="col-span-1 text-right tabular-nums font-bold text-[#B38138]">{fmtNum(r.paid)}</div>
                    <div className={`col-span-1 text-right tabular-nums font-semibold ${r.conv_paid_pct > 0 ? 'text-[#15803D]' : 'text-[#A1A1AA]'}`}>
                      {r.conv_paid_pct}%
                    </div>
                    <div className={`col-span-2 text-right tabular-nums font-bold ${r.mrr > 0 ? 'text-[#15803D]' : 'text-[#A1A1AA]'}`}>
                      ${r.mrr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className={`col-span-2 text-right tabular-nums ${r.arr > 0 ? 'text-[#0F0F10] font-semibold' : 'text-[#A1A1AA]'}`}>
                      ${r.arr.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/yr
                    </div>
                  </div>
                );
              })}
              <p className="px-4 py-2.5 text-[10px] text-[#8A8A93] bg-white/40">
                MRR = sum of monthly recurring revenue from active subscribers attributed to this campaign (yearly subs counted as price/12). Compare against your ad spend to compute ROAS.
              </p>
            </div>
          </div>
        )}
      </Section>

      {/* Geo World Map */}
      <Section title="Where visitors are coming from" icon={Globe}>
        <GeoMap points={data?.geo_points || []} />
      </Section>

      <p className="px-5 text-[10px] text-[#8A8A93] mt-8">
        Tracking is anonymous-first (cookie-less aside from a generated `mtk_anon_id` UUID in localStorage). Admin views are excluded. Geo via ipapi.co, cached 30 days per IP.
      </p>
    </div>
  );
}


// ==================== Demographics + Dates ====================

function DemoStat({ label, value, sub, icon: Icon, tint = '#0F0F10', testId, spark }) {
  const hasSpark = Array.isArray(spark) && spark.length >= 2;
  const gradId = `sparkGrad-${(testId || label).replace(/[^a-z0-9]/gi, '')}`;
  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-white border border-[var(--hairline)] p-4 relative overflow-hidden"
    >
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={13} strokeWidth={2} style={{ color: tint }} />}
        <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold">{label}</p>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[24px] font-bold tabular-nums leading-none" style={{ color: tint }}>{value}</p>
          {sub && <p className="text-[11px] text-[#8A8A93] mt-1.5">{sub}</p>}
        </div>
        {hasSpark && (
          <div className="w-[70px] h-[36px] flex-shrink-0" data-testid={`${testId}-sparkline`}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={tint} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={tint} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <ReTooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #00000014', fontSize: 11, padding: '4px 8px' }}
                  labelStyle={{ fontSize: 10, color: '#8A8A93' }}
                  formatter={(v) => [v, 'count']}
                />
                <Area type="monotone" dataKey="count" stroke={tint} strokeWidth={1.5} fill={`url(#${gradId})`} isAnimationActive={false} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function GenderBar({ male, female, other = 0, height = 6 }) {
  const total = Math.max(1, male + female + other);
  const mPct = (male / total) * 100;
  const fPct = (female / total) * 100;
  const oPct = (other / total) * 100;
  return (
    <div className="w-full rounded-full overflow-hidden flex" style={{ height, background: '#F0F0F5' }}>
      <div style={{ width: `${mPct}%`, background: '#3B82F6' }} />
      <div style={{ width: `${fPct}%`, background: '#EC4899' }} />
      {other > 0 && <div style={{ width: `${oPct}%`, background: '#B38138' }} />}
    </div>
  );
}

function DemographicsSection({ demo, spark }) {
  const g = demo?.gender || {};
  const totals = g.totals || {};
  const active = g.active_30d || {};
  const today = g.new_today || {};
  const dates = demo?.dates || {};
  const matches = demo?.matches || {};
  const sparkSeries = spark?.series || {};

  const fmtNum = (n) => (n || 0).toLocaleString();
  const fmtPct = (num, den) => {
    const total = Math.max(1, num + den);
    return `${Math.round((num / total) * 100)}% / ${Math.round((den / total) * 100)}%`;
  };

  return (
    <div className="px-5 mb-8" data-testid="demographics-section">
      <div className="flex items-center gap-2 mb-4">
        <Users size={14} className="text-[#0F0F10]" strokeWidth={2}/>
        <h2 className="font-brand text-[15px] font-[600] text-[#0F0F10]">Demographics &amp; Dates</h2>
        {demo?.date_range && demo.date_range !== 'lifetime' && (
          <span data-testid="demographics-range-pill" className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full bg-[#0F0F10] text-white">
            Last {demo.date_range}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <DemoStat label="Active men (30d)"     value={fmtNum(active.male)}   icon={User}      tint="#3B82F6" testId="stat-active-men"   spark={sparkSeries.active_male} />
        <DemoStat label="Active women (30d)"   value={fmtNum(active.female)} icon={UserCheck} tint="#EC4899" testId="stat-active-women" spark={sparkSeries.active_female} />
        <DemoStat label="New men today"        value={fmtNum(today.male)}    icon={User}      tint="#3B82F6" testId="stat-new-men"      spark={sparkSeries.new_male} />
        <DemoStat label="New women today"      value={fmtNum(today.female)}  icon={UserCheck} tint="#EC4899" testId="stat-new-women"    spark={sparkSeries.new_female} />
      </div>

      <div className="rounded-2xl bg-white border border-[var(--hairline)] p-4 mb-4" data-testid="gender-total-split">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold">Overall gender split</p>
          <p className="text-[12px] tabular-nums text-[#4B4B52]">
            <span className="text-[#3B82F6] font-semibold">{fmtNum(totals.male)}</span>
            {' \u00b7 '}
            <span className="text-[#EC4899] font-semibold">{fmtNum(totals.female)}</span>
            {totals.other > 0 && <> {' \u00b7 '}<span className="text-[#B38138] font-semibold">{fmtNum(totals.other)}</span></>}
            <span className="text-[#8A8A93]"> of {fmtNum(totals.all)}</span>
          </p>
        </div>
        <GenderBar male={totals.male || 0} female={totals.female || 0} other={totals.other || 0} height={10} />
        <p className="text-[11px] text-[#8A8A93] mt-2">{fmtPct(totals.male || 0, totals.female || 0)} men / women</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <DemoStat
          label="Matches per user"
          value={(matches.per_user_avg || 0).toFixed(2)}
          sub={`${fmtNum(matches.total)} total matches`}
          icon={Heart}
          tint="#B91C1C"
          testId="stat-matches-per-user"
        />
        <DemoStat
          label="Dates scheduled"
          value={fmtNum(dates.scheduled_total)}
          sub={`${fmtNum(dates.scheduled_7d)} in last 7d \u00b7 ${fmtNum(dates.scheduled_30d)} in last 30d`}
          icon={Calendar}
          tint="#0F0F10"
          testId="stat-dates-scheduled"
        />
        <DemoStat
          label="Date completion rate"
          value={`${dates.completion_rate_pct || 0}%`}
          sub={`${fmtNum(dates.completed_total)} of ${fmtNum(dates.scheduled_total)} checked in`}
          icon={Activity}
          tint="#15803D"
          testId="stat-date-completion"
        />
      </div>

      <div className="rounded-2xl bg-white border border-[var(--hairline)] p-4 mb-4" data-testid="ratio-by-city">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={12} className="text-[#8A8A93]" strokeWidth={2}/>
          <p className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold">Ratio by city (top 10)</p>
        </div>
        {(demo.by_city || []).length === 0 ? (
          <p className="text-[12px] text-[#8A8A93]">No city data yet.</p>
        ) : (
          <div className="space-y-2.5">
            {(demo.by_city || []).map((c) => (
              <div key={c.city} className="flex items-center gap-3" data-testid={`ratio-city-${c.city}`}>
                <p className="text-[12.5px] font-medium text-[#0F0F10] w-36 truncate">{c.city}</p>
                <div className="flex-1"><GenderBar male={c.male} female={c.female} other={c.other} /></div>
                <p className="text-[11px] tabular-nums text-[#4B4B52] w-20 text-right">
                  <span className="text-[#3B82F6]">{c.male}</span>
                  <span className="text-[#8A8A93]"> / </span>
                  <span className="text-[#EC4899]">{c.female}</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white border border-[var(--hairline)] p-4" data-testid="ratio-by-age">
        <div className="flex items-center gap-2 mb-3">
          <Users size={12} className="text-[#8A8A93]" strokeWidth={2}/>
          <p className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold">Ratio by age group</p>
        </div>
        {(demo.by_age || []).length === 0 ? (
          <p className="text-[12px] text-[#8A8A93]">No age data yet.</p>
        ) : (
          <div className="space-y-2.5">
            {(demo.by_age || []).map((row) => (
              <div key={row.bucket} className="flex items-center gap-3" data-testid={`ratio-age-${row.bucket}`}>
                <p className="text-[12.5px] font-medium text-[#0F0F10] w-20">{row.bucket}</p>
                <div className="flex-1"><GenderBar male={row.male} female={row.female} /></div>
                <p className="text-[11px] tabular-nums text-[#4B4B52] w-20 text-right">
                  <span className="text-[#3B82F6]">{row.male}</span>
                  <span className="text-[#8A8A93]"> / </span>
                  <span className="text-[#EC4899]">{row.female}</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

